import React from 'react';
import { useEntityData } from '../../../../EntityContext';
import { EditableSchemaMetadata, SchemaMetadata } from '../../../../../../../types.generated';
import { useAccessibility } from '../../../../../../../AccessibilityHandler';
import GovernanceFlagGroup from '../components/GovernanceFlagGroup';

/**
 * This hook is used to render the governance flags for a field in the schema table
 * @param editableSchemaMetadata the editable schema metadata for the entity
 * @param readOnly whether the entity is read-only
 * @param disabledTooltip optional tooltip when the control is disabled (e.g. RBAC permission message)
 * @returns a function that can be used to render the governance flags for a field
 */
export default function useGovernanceFlagRenderer(
    editableSchemaMetadata: EditableSchemaMetadata | null | undefined,
    schemaMetadata: SchemaMetadata | null | undefined,
    tagHoveredIndex: string | undefined,
    refetch,
    readOnly?: boolean,
    disabledTooltip?: string,
) {
    const { entityData } = useEntityData();
    const isAccessibilityEnabled: boolean = useAccessibility();

    return (_: any, field: any): JSX.Element => {
        // Find the governance flags for the field in the editable schema metadata
        const editableGovernanceFlags = editableSchemaMetadata?.editableSchemaFieldInfo.find(
            (editableFieldInfo) => editableFieldInfo.fieldPath === field.fieldPath,
        );

        const unEditableGovernanceFlags = schemaMetadata?.fields?.find(
            (schemaFieldInfo) => schemaFieldInfo.fieldPath === field.fieldPath,
        );

        const canAddFlag = tagHoveredIndex === field.fieldPath || isAccessibilityEnabled;

        return (
            <GovernanceFlagGroup
                editableFlags={editableGovernanceFlags?.governanceFlags?.flags}
                uneditableFlags={unEditableGovernanceFlags?.governanceFlags?.flags}
                fieldPath={field.fieldPath}
                entityUrn={entityData?.urn || ''}
                entityData={entityData}
                canAddFlag={canAddFlag}
                refetch={refetch}
                readOnly={readOnly}
                disabledTooltip={disabledTooltip}
            />
        );
    };
}
